/**
 * Viblo: the Give feedback button and (if switched on in the Viblo dashboard)
 * session recording. Loaded for signed-in members only; visitors never load
 * it. What it shows and records is controlled from the Viblo dashboard, not
 * from code. Setup notes: https://viblo.com/agent-setup.md
 *
 * REACT_APP_VIBLO_KEY is the installation's public key (vb_public_…, safe in
 * the browser). Each environment has its own installation, so recordings are
 * labelled staging or production; without a key Viblo isn't loaded.
 */
const VIBLO_SRC = 'https://app.viblo.com/v1/viblo.js';
const SCRIPT_ID = 'viblo-sdk';

// On-screen text that must not appear in recordings. Typed values are always
// masked by Viblo; these cover what is displayed: members' real names once
// introduced, emails, phone numbers, addresses (marked data-private), and the
// payment card forms.
const VIBLO_CONFIG = {
  mask: ['[data-private]'],
  block: ['[class*="StripePaymentForm_"]', '[class*="SavedCardDetails_"]'],
};

const hasWindow = () => typeof window !== 'undefined';

/** Viblo's own stub: queues calls made before the script has loaded. */
const ensureStub = () => {
  window.Viblo =
    window.Viblo ||
    function() {
      (window.Viblo.q = window.Viblo.q || []).push(arguments);
    };
};

const viblo = (...args) => {
  if (!hasWindow()) {
    return;
  }
  ensureStub();
  window.Viblo(...args);
};

/** Add the script once. Its config must be in place before it runs. */
export const loadViblo = key => {
  if (!hasWindow() || !key || document.getElementById(SCRIPT_ID)) {
    return;
  }
  ensureStub();
  window.VibloConfig = VIBLO_CONFIG;
  const script = document.createElement('script');
  script.id = SCRIPT_ID;
  script.defer = true;
  script.src = VIBLO_SRC;
  script.setAttribute('data-key', key);
  document.body.appendChild(script);
};

/** Who is giving feedback, from the signed-in Sharetribe user. Pure, for tests. */
export const vibloIdentity = currentUser => {
  const { email, profile = {} } = currentUser?.attributes || {};
  const name = [profile.firstName, profile.lastName].filter(Boolean).join(' ');
  return {
    user: { id: currentUser?.id?.uuid, email, name: name || profile.displayName || undefined },
    metadata: {
      plan: profile.metadata?.subscriptionName || profile.metadata?.subscriptionStatus || null,
      role: profile.metadata?.isAdmin ? 'admin' : profile.publicData?.userType || 'member',
      verified: !!profile.publicData?.isVerified,
    },
  };
};

export const identifyViblo = currentUser => {
  const { user, metadata } = vibloIdentity(currentUser);
  if (!user.id) {
    return;
  }
  viblo('identify', user);
  viblo('setMetadata', metadata);
};

/** On sign-out: forget the member. Only does anything if Viblo was loaded. */
export const resetViblo = () => {
  if (hasWindow() && typeof window.Viblo === 'function') {
    window.Viblo('reset');
  }
};
